import { Button, Card } from "@procurement-agents/ui";
import type { ConfirmPrefillPayload, ConfirmPrefillResume } from "@procurement-agents/scope-api";
import { useEffect, useRef, useState } from "react";
import { AutoTextarea } from "./AutoTextarea";

type FormQuestion = NonNullable<ConfirmPrefillPayload["remaining"]>[number];
const asText = (v: unknown) => (Array.isArray(v) ? v.join(", ") : v == null ? "" : String(v));

/**
 * The prefill card: what was read from the description (editable; emptied = ask me in the chat), then two ways to
 * skip the one-question-at-a-time chat: answer the remaining questions in a form here, or download them as a Word
 * questionnaire to fill in offline and attach to the next reply.
 */
export type DraftValues = Record<string, string | string[]>;
const SAVE_DELAY_MS = 800;

export function ConfirmPrefill({ payload, onSubmit, busy, onDownloadQuestions, currency, draft, onSaveDraft }: { payload: ConfirmPrefillPayload; onSubmit: (value: ConfirmPrefillResume) => void; busy: boolean; onDownloadQuestions?: () => Promise<void> | void; currency?: string; draft?: DraftValues | null; onSaveDraft?: (values: DraftValues) => Promise<unknown> }) {
  const remaining = payload.remaining ?? [];
  const itemIds = new Set(payload.items.map((i) => i.question_id));
  // a saved draft (unsent values from an earlier visit) seeds the card: corrections for noted items, answers for the form
  const [values, setValues] = useState<Record<string, string>>(() => Object.fromEntries(payload.items.map((i) => [i.question_id, typeof draft?.[i.question_id] === "string" ? (draft[i.question_id] as string) : asText(i.value)])));
  const [form, setForm] = useState<DraftValues>(() => Object.fromEntries(Object.entries(draft ?? {}).filter(([k]) => !itemIds.has(k) && remaining.some((q) => q.question_id === k))));
  const [showForm, setShowForm] = useState(() => Object.keys(form).length > 0);
  const [downloading, setDownloading] = useState(false);
  const [saveState, setSaveState] = useState<"idle" | "dirty" | "saving" | "saved" | "failed">("idle");
  const timer = useRef<number | null>(null);
  const sent = useRef(false);
  const current = () => {
    const out: DraftValues = {};
    for (const it of payload.items) { const v = values[it.question_id] ?? ""; if (v !== asText(it.value)) out[it.question_id] = v; }
    for (const [k, v] of Object.entries(form)) { if (Array.isArray(v) ? v.length : v.trim()) out[k] = v; }
    return out;
  };
  const snapshot = JSON.stringify(current());
  const savedSnapshot = useRef(JSON.stringify(Object.fromEntries(Object.entries(draft ?? {}))));
  useEffect(() => {                                         // autosave, debounced, only when something changed since the last save
    if (!onSaveDraft || snapshot === savedSnapshot.current || sent.current) return;
    setSaveState("dirty");
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(async () => {
      setSaveState("saving");
      try { await onSaveDraft(JSON.parse(snapshot)); savedSnapshot.current = snapshot; setSaveState("saved"); } catch { setSaveState("failed"); }
    }, SAVE_DELAY_MS);
    return () => { if (timer.current) window.clearTimeout(timer.current); };
  }, [snapshot, onSaveDraft]);
  const submit = () => {
    sent.current = true;
    if (timer.current) window.clearTimeout(timer.current);
    const out: Record<string, string | string[] | null> = {};
    for (const it of payload.items) {
      const v = values[it.question_id] ?? "";
      if (v !== asText(it.value)) out[it.question_id] = v.trim() ? v : null;    // emptied: clear it and ask in the chat
    }
    if (showForm) for (const [k, v] of Object.entries(form)) { if (Array.isArray(v) ? v.length : v.trim()) out[k] = v; }
    onSubmit(Object.keys(out).length ? out : "confirm");
  };
  const saveLine = { idle: null, dirty: "Unsaved changes…", saving: "Saving…", saved: "Draft saved — you can come back and finish later.", failed: "Could not save the draft; check your connection." }[saveState];
  const download = async () => { if (!onDownloadQuestions) return; setDownloading(true); try { await onDownloadQuestions(); } finally { setDownloading(false); } };
  const answered = Object.values(form).filter((v) => (Array.isArray(v) ? v.length : v.trim())).length;
  return (
    <Card title="From your description, I have already noted:">
      <ul className="prefill">
        {payload.items.map((it) => (
          <li key={it.question_id}>
            <div className="muted small">{it.prompt}</div>
            <AutoTextarea value={values[it.question_id] ?? ""} onChange={(e) => setValues((v) => ({ ...v, [it.question_id]: e.target.value }))} aria-label={it.prompt} />
          </li>
        ))}
      </ul>
      <p className="muted small">Correct anything above, or empty a field and I will ask you in the chat.</p>
      {remaining.length > 0 && (
        <div className="prefill-options">
          <span className="muted small">{remaining.length} more question{remaining.length === 1 ? "" : "s"} to go. You can answer them one at a time in the chat, or:</span>
          <div className="row">
            <button type="button" className="link" aria-pressed={showForm} onClick={() => setShowForm((s) => !s)}>{showForm ? "Hide the form" : "Answer them all in a form"}</button>
            {onDownloadQuestions && <button type="button" className="link" onClick={download} disabled={downloading}>{downloading ? "Preparing…" : "Download the questions (Word)"}</button>}
          </div>
          {showForm && (
            <div className="prefill-form" role="group" aria-label="Remaining questions">
              {remaining.map((q) => <FormField key={q.question_id} q={q} value={form[q.question_id]} onChange={(v) => setForm((f) => ({ ...f, [q.question_id]: v }))} currency={currency} />)}
              <p className="muted small">Leave any blank and I will ask you in the chat.</p>
            </div>
          )}
          {!showForm && onDownloadQuestions && <p className="muted small">Downloaded questions can be answered offline: fill in the document and attach it to your next reply.</p>}
        </div>
      )}
      <div className="row between">
        <Button variant="primary" onClick={submit} disabled={busy}>{showForm && answered ? `Confirm and send ${answered} answer${answered === 1 ? "" : "s"}` : "Confirm"}</Button>
        {saveLine && <span className={`muted small draft-state ${saveState}`} aria-live="polite">{saveLine}</span>}
      </div>
    </Card>
  );
}

function FormField({ q, value, onChange, currency }: { q: FormQuestion; value: string | string[] | undefined; onChange: (v: string | string[]) => void; currency?: string }) {
  const label = <span className="field-label">{q.prompt}{!q.required && <span className="muted"> (optional)</span>}</span>;
  if (q.answer_type === "boolean" || q.answer_type === "choice") {
    const opts = q.answer_type === "boolean" ? ["yes", "no"] : q.choices ?? [];
    return <label className="field">{label}<select value={typeof value === "string" ? value : ""} onChange={(e) => onChange(e.target.value)} aria-label={q.prompt}><option value="">— leave for the chat —</option>{opts.map((c) => <option key={c} value={c}>{c}</option>)}</select></label>;
  }
  if (q.answer_type === "multi_choice") {
    const picked = Array.isArray(value) ? value : [];
    return (
      <fieldset className="field">
        <legend className="field-label">{q.prompt}</legend>
        <div className="checks">
          <label className="check all"><input type="checkbox" checked={picked.length === (q.choices ?? []).length && picked.length > 0} onChange={(e) => onChange(e.target.checked ? [...(q.choices ?? [])] : [])} /> <strong>All</strong></label>
          {(q.choices ?? []).map((c) => <label key={c} className="check"><input type="checkbox" checked={picked.includes(c)} onChange={(e) => onChange(e.target.checked ? [...picked, c] : picked.filter((x) => x !== c))} /> {c}</label>)}
        </div>
      </fieldset>
    );
  }
  if (q.answer_type === "number") return <label className="field">{label}<div className="prefixed"><span className="prefix">{currency ?? ""}</span><input inputMode="decimal" value={typeof value === "string" ? value : ""} onChange={(e) => onChange(e.target.value)} placeholder="2,100,000 or 2.1m" aria-label={q.prompt} /></div></label>;
  if (q.answer_type === "date") return <label className="field">{label}<AutoTextarea value={typeof value === "string" ? value : ""} onChange={(e) => onChange(e.target.value)} placeholder="1 July 2027, July 2027, Q3 2027…" aria-label={q.prompt} /></label>;
  return <label className="field">{label}<AutoTextarea minRows={2} value={typeof value === "string" ? value : ""} onChange={(e) => onChange(e.target.value)} aria-label={q.prompt} /></label>;
}
